import React from 'react';

import { Box, IconButton, styled } from '@mui/material';

import trainerIcon from 'Assets/images/trainerIcon.svg';
import { LoginState, useSelectLoginState } from 'Auth/data';
import {
  ConnectToFreshStartDesktopButton,
  ConnectToMyDesktopButton,
  ConnectToParticipantDesktopButton,
  ConnectToTrainerDesktopButton
} from 'Common/ConnectToDesktopButtons';
import type { CourseModel } from 'Courses/data';
import { useAppDispatch } from 'Store';
import { Colors } from 'Themes/colors';
import { saveIsMachineBarPinned, useSelectIsMachineBarPinned } from 'User/data';
import type { UserDisplayModel } from 'User/userDisplayModel';

import { CloseIcon, IconSizes, PushPinIcon } from '@libs/shared/ui/icons';

import { LivePreview } from '../shared/live-preview-card/live-preview/live-preview';
import { UserInfo } from '../shared/live-preview-card/live-preview/user-info';
import { ActionWrapper, PushPinButton } from '../shared/live-preview-card/live-preview-card.styles';

import { useDesktopFullscreenSafe } from './desktop-fullscreen-context';

import type { DesktopModel } from 'Api';

export const ViewerContainer = styled(Box)`
  background-color: ${Colors.lightGrey};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  outline: none;
`;

export const ContentArea = styled(Box)`
  flex: 1;
  position: relative;
  overflow: hidden;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CloseButton = styled(IconButton)`
  padding: 4px;
  color: ${Colors.darkGrey};
  &:hover {
    color: ${Colors.black};
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

interface SharedFullscreenViewerProps {
  isFullscreen: boolean;
  isBrowserFullscreen?: boolean;
  currentDesktop: DesktopModel;
  currentCourse: CourseModel;
  currentUser: UserDisplayModel;
  isFreshStartDesktop: boolean;
  isParticipantDesktop: boolean;
  isTrainerDesktop: boolean;
  exitFullscreen: () => void;
  shouldRenderContent?: boolean;
  isSwitching?: boolean;
  showSwitcher?: boolean;
  switcherComponent?: React.ReactNode;
}

export const SharedFullscreenViewer: React.FC<SharedFullscreenViewerProps> = ({
  isFullscreen,
  isBrowserFullscreen = false,
  currentDesktop,
  currentCourse,
  currentUser,
  isFreshStartDesktop,
  isParticipantDesktop,
  isTrainerDesktop,
  exitFullscreen,
  shouldRenderContent = true,
  isSwitching = false,
  showSwitcher = false,
  switcherComponent
}) => {
  const { viewerRef } = useDesktopFullscreenSafe() ?? {};
  const dispatch = useAppDispatch();
  const isMachineBarPinned = useSelectIsMachineBarPinned();
  const loginState = useSelectLoginState();
  const isLogged = loginState === LoginState.LOGGED;

  const handleSaveIsMachineBarPinned = () => {
    dispatch(saveIsMachineBarPinned({ isMachineBarPinned: !isMachineBarPinned, isLogged }));
  };

  let connectToDesktopButton: React.ReactNode = null;

  if (isFreshStartDesktop) {
    connectToDesktopButton = (
      <ConnectToFreshStartDesktopButton
        courseId={currentCourse.id}
        isFreshStartMachineReadyToClone={currentCourse.isFreshStartMachineReadyToClone}
      />
    );
  } else if (isParticipantDesktop) {
    connectToDesktopButton = (
      <ConnectToParticipantDesktopButton
        courseId={currentCourse.id}
        participantId={currentUser.id as string}
        isHighlighted={false}
        isMinimized={false}
      />
    );
  } else if (isTrainerDesktop) {
    if (currentCourse.isTrainer) {
      connectToDesktopButton = (
        <ConnectToMyDesktopButton
          courseId={currentCourse.id}
          isHighlighted={false}
          isMinimized={false}
        />
      );
    } else {
      connectToDesktopButton = (
        <ConnectToTrainerDesktopButton
          courseId={currentCourse.id}
          isHighlighted={false}
          isMinimized={false}
        />
      );
    }
  }

  const isTrainerDesktopPreview = isTrainerDesktop && !currentCourse.isTrainer;
  const trainerAvatar = <img src={trainerIcon} alt="trainer icon" />;

  const customAvatar =
    isTrainerDesktop || isTrainerDesktopPreview ? trainerAvatar : currentUser.customAvatar;

  const displayCondition =
    (isFullscreen || isBrowserFullscreen) && (shouldRenderContent || isSwitching);

  return (
    <ViewerContainer
      ref={viewerRef}
      tabIndex={-1}
      style={{ display: displayCondition ? 'flex' : 'none' }}
    >
      <ActionWrapper fullScreen={true} pinEnabled={isMachineBarPinned} isMinimized={false}>
        <PushPinButton pinEnabled={isMachineBarPinned} onClick={handleSaveIsMachineBarPinned}>
          <PushPinIcon />
        </PushPinButton>

        <UserInfo
          name={currentUser.displayName}
          avatarColor={currentUser.avatarColor ?? ''}
          avatar={currentUser.avatar}
          customAvatar={customAvatar}
        />

        {showSwitcher && switcherComponent}

        <CloseButton onClick={exitFullscreen} title="Exit Fullscreen (Esc)" size="small">
          <CloseIcon fontSize={IconSizes.small} />
        </CloseButton>
      </ActionWrapper>

      <ContentArea>
        {isSwitching ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'white',
              fontSize: '18px'
            }}
          >
            Switching desktop...
          </div>
        ) : shouldRenderContent ? (
          <LivePreview
            fullscreenModeIsEnabled={true}
            settingsOpened={false}
            course={currentCourse}
            desktop={currentDesktop}
            isParticipantDesktop={isParticipantDesktop}
            isTrainerDesktop={isTrainerDesktop}
            isTrainerDesktopPreview={isTrainerDesktopPreview}
            connectToDesktopButton={connectToDesktopButton}
            participantsMachineControlActive={currentCourse.participantsMachineControlActive}
          />
        ) : null}
      </ContentArea>
    </ViewerContainer>
  );
};
